import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Phone, Mail, MessageCircle, Building, BadgeCheck, LogOut, HelpCircle, Save } from "lucide-react";
import { toast } from "sonner";

export default function More() {
  const { user } = useOutletContext();
  const { logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [wa, setWa] = useState(user?.whatsapp_number || "");
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", { name, whatsapp_number: wa });
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const rows = [
    { icon: BadgeCheck, label: "Employee ID", value: user?.employee_id },
    { icon: Mail, label: "Email", value: user?.email },
    { icon: Phone, label: "Mobile", value: user?.mobile },
    { icon: Building, label: "Team", value: user?.team || "—" },
  ];

  return (
    <div className="space-y-5">
      <header className="flex items-center gap-3">
        <div className="h-14 w-14 rounded-2xl bg-primary/15 flex items-center justify-center font-heading text-xl font-extrabold text-primary">
          {(user?.name || "U")[0]}
        </div>
        <div>
          <h1 className="font-heading text-xl font-extrabold">{user?.name}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-400 capitalize">{user?.account_status}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400 capitalize">Telecaller</span>
          </div>
        </div>
      </header>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 divide-y divide-slate-800">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3 px-4 py-3.5">
            <r.icon className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-400 flex-1">{r.label}</span>
            <span className="text-sm font-medium">{r.value}</span>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 space-y-3">
        <h2 className="font-heading font-semibold text-sm flex items-center gap-2"><User className="h-4 w-4 text-primary" />Edit Profile</h2>
        <div>
          <Label className="text-xs text-slate-500">Display Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="bg-slate-950/60 border-slate-700 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 flex items-center gap-1"><MessageCircle className="h-3 w-3" />WhatsApp Number</Label>
          <Input value={wa} onChange={(e) => setWa(e.target.value)} data-testid="profile-whatsapp" className="bg-slate-950/60 border-slate-700 mt-1 font-mono" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-profile" className="w-full bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save</Button>
      </section>

      <button className="w-full rounded-2xl border border-slate-800 bg-slate-900/50 px-4 py-3.5 flex items-center gap-3 text-slate-300">
        <HelpCircle className="h-4 w-4 text-slate-500" /> Help & Support
      </button>

      <Button onClick={doLogout} data-testid="logout-button" variant="outline" className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10">
        <LogOut className="h-4 w-4 mr-1.5" /> Logout
      </Button>
    </div>
  );
}
